import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Settings as Cog, Save, Mail, MessageSquare, CheckCircle2, AlertCircle } from "lucide-react";
import { ApiAccounts } from "@/components/ApiAccounts";
import { WhatsAppSettings } from "@/components/WhatsAppSettings";
import { SecretValue } from "@/components/SecretValue";
import { ChatSettings } from "@/components/chat/ChatSettings";
import { AppSitesSettings } from "@/components/chat/AppSitesSettings";

export default function Settings() {
  const [s, setS] = useState(null);
  const [statsKey, setStatsKey] = useState(0);
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-slate-400">Carregando...</div>;

  const save = async () => {
    try {
      await api.put("/settings", {
        from_name: s.from_name, reply_to: s.reply_to || null,
        company_name: s.company_name, pix_key: s.pix_key,
        low_credits_threshold: parseInt(s.low_credits_threshold ?? 500) || 500,
        sms_warn_threshold: parseInt(s.sms_warn_threshold ?? 300) || 300,
        sms_crit_threshold: parseInt(s.sms_crit_threshold ?? 100) || 100,
        ignore_resellers: (s._ignore_text ?? (s.ignore_resellers || []).join(", "))
          .split(",").map((x) => x.trim()).filter(Boolean),
        allow_resellers: (s._allow_text ?? (s.allow_resellers || []).join(", "))
          .split(",").map((x) => x.trim()).filter(Boolean),
        reseller_tokens: s._tokens || {},
      });
      toast.success("Configurações salvas");
      api.get("/settings").then((r) => setS(r.data));
      setStatsKey((k) => k + 1);
    } catch { toast.error("Erro ao salvar"); }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="text-3xl font-bold tracking-tight text-slate-100 flex items-center gap-2 mb-6"><Cog className="h-7 w-7 text-sky-400" /> Configurações</h1>

      <div className="bg-[#111827] border border-[#1F293D] rounded-xl p-5 mb-5">
        <h3 className="text-base font-semibold text-slate-200 flex items-center gap-2 mb-4"><Mail className="h-5 w-5 text-sky-400" /> E-mail (Resend)</h3>
        <div className="flex items-center gap-2 mb-4 text-sm text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2">
          <CheckCircle2 className="h-4 w-4" /> Envio de e-mail ATIVO — {s.email_provider}. Não é necessária API key.
        </div>
        <div className="space-y-3">
          <L label="Nome do remetente (aparece no e-mail)"><input data-testid="settings-from-name" value={s.from_name || ""} onChange={set("from_name")} className={inp} /></L>
          <L label="E-mail de resposta (reply-to) — opcional"><input data-testid="settings-reply-to" value={s.reply_to || ""} onChange={set("reply_to")} placeholder="contato@suaempresa.com" className={inp} /></L>
        </div>
      </div>

      <div className="bg-[#111827] border border-[#1F293D] rounded-xl p-5 mb-5">
        <h3 className="text-base font-semibold text-slate-200 flex items-center gap-2 mb-4"><MessageSquare className="h-5 w-5 text-amber-400" /> SMS · SMS Mais</h3>
        <div className="flex items-center gap-2 text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2 mb-3">
          <CheckCircle2 className="h-4 w-4" /> Envio real ativo no botão <b className="mx-1">Cobrança SMS</b> (ficha do cliente e Faturas).
        </div>
        <label data-testid="settings-sms-auto" className="flex items-center justify-between gap-3 rounded-lg border border-[#1F293D] bg-[#0F141F] px-3 py-2.5 cursor-pointer">
          <span className="text-sm text-slate-300">SMS real nas automações diárias
            <span className="block text-xs text-slate-500">{s.sms_auto_enabled ? "Ligado: as regras enviam SMS de verdade (consome saldo)." : "Desligado: as regras registram o SMS como simulado."}</span>
          </span>
          <input data-testid="settings-sms-auto-toggle" type="checkbox" className="h-5 w-5 accent-emerald-500" checked={!!s.sms_auto_enabled}
            onChange={async (e) => { const v = e.target.checked; await api.put("/settings", { sms_auto_enabled: v }); setS({ ...s, sms_auto_enabled: v }); toast.success(v ? "SMS real ligado nas automações" : "Automações voltaram ao modo simulado"); }} />
        </label>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <L label="Aviso amarelo: saldo abaixo de (SMS)"><input data-testid="settings-sms-warn" type="number" min="1" value={s.sms_warn_threshold ?? 300} onChange={set("sms_warn_threshold")} className={inp} /></L>
          <L label="Aviso vermelho: saldo abaixo de (SMS)"><input data-testid="settings-sms-crit" type="number" min="1" value={s.sms_crit_threshold ?? 100} onChange={set("sms_crit_threshold")} className={inp} /></L>
        </div>
        <div className="mt-3 text-xs text-slate-400">
          Status de entrega: no SMS Mais em <b>API & Integrações → Webhooks</b>, evento <b>Status de entrega (DLR)</b>, cadastre:
          <div data-testid="settings-dlr-url" className="mt-1 font-mono text-sky-300 break-all">{process.env.REACT_APP_BACKEND_URL}{s.sms_dlr_url}</div>
          <div className="mt-1">Token de autenticação: <SecretValue testid="settings-dlr-token" masked={s.sms_dlr_token} field="sms_dlr_token" /></div>
        </div>
      </div>

      <WhatsAppSettings s={s} setS={setS} />
      <ChatSettings />
      <AppSitesSettings />

      <div className="bg-[#111827] border border-[#1F293D] rounded-xl p-5 mb-5">
        <h3 className="text-base font-semibold text-slate-200 mb-4">Automação & Empresa</h3>
        <div className="grid grid-cols-2 gap-3">
          <L label="Nome da empresa"><input data-testid="settings-company" value={s.company_name || ""} onChange={set("company_name")} className={inp} /></L>
          <L label="Horário dos envios automáticos (0-23h)"><input data-testid="settings-hour" readOnly value="09:00 (Brasília) · automático" className={`${inp} opacity-70`} /></L>
          <L label="Chave PIX (opcional)"><input data-testid="settings-pix" value={s.pix_key || ""} onChange={set("pix_key")} className={inp} /></L>
          <L label="Alerta de créditos baixos (abaixo de)"><input data-testid="settings-low-credits" type="number" min="1" value={s.low_credits_threshold ?? 500} onChange={set("low_credits_threshold")} className={inp} /></L>
        </div>
      </div>

      <div className="bg-[#111827] border border-[#1F293D] rounded-xl p-5 mb-5">
        <h3 className="text-base font-semibold text-slate-200 mb-2">Revendedores permitidos</h3>
        <p className="text-xs text-slate-400 mb-3">Somente clientes destes revendedores entram no CRM (somados). Separe por vírgula. Deixe vazio para aceitar todos.</p>
        <textarea data-testid="settings-allow" rows={2}
          value={s._allow_text ?? (s.allow_resellers || []).join(", ")}
          onChange={(e) => setS({ ...s, _allow_text: e.target.value })}
          placeholder="csalpha, alpha2, wetester, alpha4" className={inp} />
        <ResellerStats key={statsKey} />
      </div>

      <div className="bg-[#111827] border border-[#1F293D] rounded-xl p-5 mb-5" data-testid="reseller-tokens-card">
        <h3 className="text-base font-semibold text-slate-200 mb-2">Tokens da API de Revenda</h3>
        <p className="text-xs text-slate-400 mb-3">Gere o token em <b>Integrações → API de Revenda → Gerar Token</b> logado em <b>cada</b> master e cole aqui. Com o token, o CRM busca clientes, WhatsApp, vencimentos, valores e link PIX direto do servidor, sem precisar da extensão. Contas sem token continuam com os dados atuais.</p>
        <div className="space-y-2">
          {(s.allow_resellers || []).map((name) => (
            <div key={name} className="flex items-center gap-3">
              <span className="w-24 font-mono text-sm text-sky-300">{name}</span>
              <input data-testid={`token-${name}`} type="password"
                placeholder={(s.reseller_tokens || {})[name] ? `salvo (${s.reseller_tokens[name]}) — cole para trocar` : "cole o token deste revendedor"}
                value={(s._tokens || {})[name] || ""}
                onChange={(e) => setS({ ...s, _tokens: { ...(s._tokens || {}), [name]: e.target.value } })}
                className={inp + " mt-0 flex-1"} />
              {(s.reseller_tokens || {})[name] && <span className="text-[11px] text-emerald-400">✓ salvo</span>}
            </div>
          ))}
        </div>
        <div className="mt-4"><ApiAccounts key={statsKey} /></div>
      </div>

      <div className="bg-[#111827] border border-[#1F293D] rounded-xl p-5 mb-5">
        <h3 className="text-base font-semibold text-slate-200 mb-2">Revendedores ignorados</h3>
        <p className="text-xs text-slate-400 mb-3">Clientes pertencentes a estes revendedores (sub-revendas) <b>não serão contabilizados</b> nem importados. Separe por vírgula. Vale também o login do próprio revendedor.</p>
        <textarea data-testid="settings-ignore" rows={2}
          value={s._ignore_text ?? (s.ignore_resellers || []).join(", ")}
          onChange={(e) => setS({ ...s, _ignore_text: e.target.value })}
          placeholder="clodotv1, tatojau, emanoell_pbc, acnet233, anaemyrella2619"
          className={inp} />
        <p className="text-[11px] text-slate-500 mt-2">Ao salvar, os clientes já importados desses revendedores também são removidos.</p>
      </div>

      <button data-testid="save-settings-button" onClick={save} className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600"><Save className="h-4 w-4" /> Salvar configurações</button>
    </div>
  );
}

function ResellerStats() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/sync/resellers").then((r) => setRows(r.data)); }, []);
  if (!rows.length) return <p className="text-[11px] text-slate-500 mt-3">Nenhum revendedor visto ainda. Rode uma sincronização.</p>;
  return (
    <div data-testid="reseller-stats" className="mt-4 max-h-56 overflow-y-auto border border-[#1F293D] rounded-lg">
      <table className="w-full text-xs">
        <thead><tr className="text-left text-slate-500 border-b border-[#1F293D]"><th className="px-3 py-2">Revendedor visto no painel</th><th className="px-3 py-2">No CRM</th><th className="px-3 py-2">Situação</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.reseller} className="border-b border-[#1F293D]">
              <td className="px-3 py-1.5 font-mono text-slate-300">{r.reseller}</td>
              <td className="px-3 py-1.5 font-mono text-slate-400">{r.clients}</td>
              <td className={`px-3 py-1.5 ${r.included ? "text-emerald-400" : "text-slate-500"}`}>{r.included ? "incluído" : "fora"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const inp = "mt-1 w-full px-3 py-2 rounded-lg bg-[#0B0F17] border border-[#1F293D] text-sm text-slate-200 focus:border-sky-500 focus:outline-none";
const L = ({ label, children }) => (<label className="block"><span className="text-[11px] uppercase tracking-wider text-slate-500">{label}</span>{children}</label>);
